import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from '../../../../core/redux/api/reduxStandardApiAction';
import { Media } from '../media';
import { apiEncodeRfid } from '../../../../core/redux/api/endpoints';
import { IAppState } from '../../../../core/redux/IAppState';
import { ACTION_TYPE } from '../../../../core/redux/actionType';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../../../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../../../../core/redux/api/circuitBreaker/module';
import * as R from 'ramda';
import { updateGuest } from '../../../module';
import { GuestDetails } from '../../../guestDetails';
import { assoc } from '../../../../language/module';


export const KEY_ACTION = ACTION_TYPE.ASSOC_RFID_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.ASSOC_RFID_REQUEST,
  ACTION_TYPE.ASSOC_RFID_RESPONSE,
  ACTION_TYPE.ASSOC_RFID_ERROR,
];


/**
 * Constructs an action that can be dispatched to associate a guest's pass with an RFID code.
 */
export const assocRfidAction = (guest: GuestDetails, media: Media) => makeApiAction(
  {
    method:   'POST',
    endpoint: apiEncodeRfid(media.mediaID),
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    //body: `"${media.chipID}"`,
    body: state => JSON.stringify(
      { 
        userId: state.userState.userId,
        chipID : media.chipID, 
        resortAbbrv : state.selectedResort.resortAbbreviation,
        operatorID : state.userState.userName,
        shouldHydrateGuest: R.includes(state.userState.role, state.resortToolsConfig.enableCustomerImport)
      }),
    types:    ACTIONS.map(action => ({
      type: action,
      meta: { guest, media },
    })) as RsaaTypes,
    bailout:  state => !!getLoading(state, guest.masterGuestId, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<any> = (state, action, error, isLoading, payload) => {
  const metaGuest = action.meta.guest;
  const guestId   = metaGuest.masterGuestId;
  const media     = R.path<Media>([ 'meta', 'media' ], action);

  if (isLoading) {
    return addLoading(state, guestId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, guestId, KEY_ACTION, error, { guest: metaGuest });
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, guestId, KEY_ACTION);

  return updateGuest(
    noLoadingOrError,
    guestId,
    guest => assoc(
      'media',
      [ media! ].concat(guest.media.filter(oldMedia => oldMedia.mediaID !== media?.mediaID)),
      guest,
    ),
  );
};


/**
 * Function that maps actions into new app state. Specifically, updates app state with information from actions related
 * to associating a guest's pass's RFID code with their account. Returns a new app state, of course.
 */
export const assocRfidReducer = makeApiReducer<any>(ACTIONS, KEY_ACTION, reducer);
